import React, { useEffect, useState } from 'react';

import { ReportTable } from '../../../components/UI/Layouts/ReportTable';
import deleteIcon from '../../../assets/icons/delete-icon.svg';
import editIcon from '../../../assets/icons/pen2.svg';
import EditServicesModal from './Modals/EditServiceModal';
import ViewServiceModal from './Modals/ViewServiceModal';
import AddServices from './Modals/AddServicesModal';
import WithLoader from '../../../components/WithLoader/WithLoader';
import classes from './AdditionalServices.module.css';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  deleteOtherService,
  getAllOtherServices,
  getOtherServices,
  OtherServices,
  selectAllOtherServices,
  selectOtherServices,
  selectOtherServicesStatus,
} from '../../../store/slices/consultationOtherServices';
import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import { useConsultationContext } from '../../../context/ConsultationContext';
import { selectLocationId } from '../../../store/slices/auth';
import { useTranslation } from '../../../hooks/useTranslations';

export interface IOtherService {
  service_id: string;
  service_name: string;
  service_type_id: string;
  work_order_other_services_log_id?: string;
  remarks?: string;
}

interface Props {
  workOrderId: number | undefined;
  isWorkOrderFinalized?: boolean;
}

const AdditionalServicesTab: React.FC<Props> = ({ workOrderId, isWorkOrderFinalized }) => {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const allOtherServicesState: OtherServices[] =
    useAppSelector<OtherServices[]>(selectAllOtherServices);
  const { addTab } = useConsultationContext();
  const otherServicesState: OtherServices[] = useAppSelector<OtherServices[]>(selectOtherServices);
  const locationId: string = useAppSelector<string>(selectLocationId);
  const otherServicesStatus: string = useAppSelector<string>(selectOtherServicesStatus);
  const [servicesAdded, setServicesAdded] = useState<IOtherService[]>([]);
  const [isEditServicesModalOpen, setIsEditServicesModalOpen] = useState(false);
  const [editServiceSelected, setEditServiceSelected] = useState<IOtherService | null>(null);
  const [isViewServicesModalOpen, setIsViewServicesModalOpen] = useState(false);
  const [viewServiceSelected, setViewServiceSelected] = useState<IOtherService | null>(null);
  const openPreview = () => {
    addTab({ value: 17, text: translations('preview'), isVisible: true });
  };
  const language = localStorage.getItem('lang') || 'en'
  useEffect(() => {
    if (workOrderId) {
      dispatch(
        getAllOtherServices({
          locationId: Number(locationId),
          language,
        })
      );
      dispatch(getOtherServices({ workOrderId: Number(workOrderId), language }));
    }
  }, [workOrderId,language]);

  return (
    <WithLoader isLoading={otherServicesStatus === 'pending'}>
      <div className={classes.container}>
        {!isWorkOrderFinalized && (
          <AddServices
            workOrderId={workOrderId}
            otherServices={allOtherServicesState.filter(
              (os) => !otherServicesState.find((aslwo) => aslwo.service_id === os.service_id)
            )}
            servicesSelected={servicesAdded}
            onAddService={(service) => {
              if (!servicesAdded?.find((s) => s.service_id === service.service_id))
                setServicesAdded([...servicesAdded, service]);
            }}
            onRemoveService={(service) =>
              setServicesAdded(servicesAdded.filter((s) => s.service_id !== service.service_id))
            }
            onServicesAddedHandler={() => {
              dispatch(getOtherServices({ workOrderId: Number(workOrderId), language }));
              setServicesAdded([]);
            }}
          />
        )}
        <div className={classes.content}>
          {otherServicesState.length === 0 ? (
            <div className={classes.noServicesAddedLabel}>{translations('no.services.added')}</div>
          ) : (
            <div className={classes.tableContainer}>
              <ReportTable
                columns={[
                  {
                    name: translations('services'),
                    path: 'service_name',
                    type: 'TEXT',
                  },
                  {
                    name: translations('remarks'),
                    path: 'remarks',
                    type: 'SHORT_TEXT',
                  },
                  {
                    name: translations('documents'),
                    path: 'documents',
                    type: 'SHORT_TEXT',
                  },
                ]}
                rows={otherServicesState}
                actions={
                  isWorkOrderFinalized
                    ? [
                        {
                          onClick: (row) => {
                            if ('work_order_other_services_log_id' in row)
                              setViewServiceSelected(row);
                            setIsViewServicesModalOpen(true);
                          },
                          displayName: translations('view'),
                        },
                      ]
                    : [
                        {
                          renderer: <div className={classes.view}>{translations('view')}</div>,
                          onClick: (row) => {
                            if ('work_order_other_services_log_id' in row)
                              setViewServiceSelected(row);
                            setIsViewServicesModalOpen(true);
                          },
                        },
                        {
                          onClick: (row) => {
                            if ('work_order_other_services_log_id' in row)
                              setEditServiceSelected(row);
                            setIsEditServicesModalOpen(true);
                          },
                          renderer: (
                            <img
                              src={editIcon}
                              alt="edit"
                              style={{ cursor: 'pointer', marginLeft: '30px' }}
                            />
                          ),
                        },
                        {
                          onClick: (row) => {
                            if ('work_order_other_services_log_id' in row)
                              dispatch(
                                deleteOtherService({
                                  logId: row.work_order_other_services_log_id,
                                  workOrderId: workOrderId,
                                })
                              );
                          },
                          renderer: (
                            <img
                              src={deleteIcon}
                              alt="delete"
                              style={{ cursor: 'pointer', marginLeft: '30px' }}
                            />
                          ),
                        },
                      ]
                }
              />
            </div>
          )}
        </div>
        <div className={classes.btnWrapper}>
          {!isWorkOrderFinalized && (
            <PrimaryButtonV2
              text={translations('preview')}
              buttonStyle={{
                width: '150px',
                height: '40px',
                backgroundColor: '#F4F7FD',
                border: '1px solid #0B1B5C',
                color: '#0B1B5C',
              }}
              onClick={openPreview}
            />
          )}
        </div>
        <EditServicesModal
          isOpen={isEditServicesModalOpen}
          onClose={() => {
            setIsEditServicesModalOpen(false);
            setEditServiceSelected(null);
          }}
          serviceSelected={editServiceSelected}
          onServiceEditHandler={() => {
            setIsEditServicesModalOpen(false);
            setEditServiceSelected(null);
            dispatch(getOtherServices({ workOrderId: Number(workOrderId), language }));
          }}
        />
        <ViewServiceModal
          isOpen={isViewServicesModalOpen}
          onClose={() => {
            setIsViewServicesModalOpen(false);
            setViewServiceSelected(null);
          }}
          serviceSelected={viewServiceSelected}
        />
      </div>
    </WithLoader>
  );
};

export default AdditionalServicesTab;
